import { inject, Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { FileFormat, RelatedToType } from '@shared/enums';
import { StateBase } from '@state/state-base';
import { Entity, TableData, Template } from '../types';
import { EMPTY, forkJoin, of, switchMap, tap, catchError, throwError, filter } from 'rxjs';
import { FieldDef, SavedView } from '@core/models';
import { UserState } from '@state/user/state';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import {
  BulkUpdateParams,
  JupiterStateFeatureSupportModel,
  JupiterStateModel,
  LabelSlot,
  PermissionSlot,
} from './types';
import { ExportOptions } from '@shared/models/export-options';
import { GraphqlConnection, GraphqlParams, GraphqlResultInfo } from '@shared/models/graphql';
import { generateDownloadableFile } from '@shared/functions';
import { Dictionary, TsSnackbarService } from 'tsui';
import { JupiterDataService } from '../@services/jupiter-data.service';
import { getLinkedFieldAsFjson } from '../@utils/functions';
import { AccountState, AccountStateModel } from '@state/account/state';
import { AccountActions } from '@state/account/actions';
import { Engagement } from 'app/modules/engagements/engagements.types';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Project, Property } from 'app/modules/projects/projects.types';
import { LoanQuote } from 'app/modules/projects/details/loans/loans.types';
import { EntityActions, ExportScheduleActions, RelatedToActions, TemplateActions, ViewActions } from '@jupiter-actions';
import { JupiterService } from '../@services/jupiter.service';
import { ObjectsManagerState, ObjectsManagerStateModel } from 'app/modules/settings/objects-manager/state/state';
import { DEFAULT_PAGE_INFO, defaultPropertyAddFormDefinition, defaultStateValue } from './consts';
import {
  addGroupAggregations,
  getFeatureSupport,
  getLabelSlots,
  isAddFormDefinitionPersisted,
  mapGroupBySearchResultsToRows,
} from './functions';
import shortUniqueId from 'short-unique-id';
import { NewPermission } from '@core/enums';
import { Router } from '@angular/router';
import { PropertyUnit } from '@modules/projects/details/leases/tenants.types';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { ExportScheduleForm } from '../home/@components/export-scheduling-management/export-schedule.types';
import { PROPERTY_ADD_FORM_FIELDS_BLACKLIST } from '@shared/modules/custom-form/custom-form/custom-form.utils';
import { cloneDeep } from 'lodash-es';

@State<JupiterStateModel>({ name: new StateToken<JupiterStateModel>('jupiterState'), defaults: defaultStateValue })
@Injectable()
export class JupiterState extends StateBase {
  private jupiterDataService = inject(JupiterDataService);
  private tsSnackbarService = inject(TsSnackbarService);
  private confirmationDialogService = inject(ConfirmationDialogService);
  private service: JupiterService;
  private router = inject(Router);

  constructor() {
    super();
  }

  static getLabelSlot(value: LabelSlot) {
    return createSelector([JupiterState], (state: JupiterStateModel) => state.slots.labels[value]);
  }

  static getPermissionSlot(value: PermissionSlot) {
    return createSelector([JupiterState], (state: JupiterStateModel) => state.slots.permissions[value]);
  }

  @Selector()
  static getTemplate(state: JupiterStateModel): Template {
    return state.entity?.template;
  }

  @Selector()
  static getRowsMemento(state: JupiterStateModel): any[] {
    return state.data.rowsMemento;
  }

  @Selector()
  static getTemplateSummary(state: JupiterStateModel): any {
    return state.entity?.template?.definition?.summary;
  }

  @Selector()
  static getEntityTabs(state: JupiterStateModel): any {
    return state.entity?.template?.definition?.tabs ?? [];
  }

  @Selector()
  static getEntity(state: JupiterStateModel): Entity {
    return state.entity;
  }

  @Selector()
  static getTransientEntity(state: JupiterStateModel): any {
    return state.transientEntity;
  }

  @Selector()
  static getData(state: JupiterStateModel): TableData<any> {
    return state.data;
  }

  @Selector()
  static getRelatedToKey(state: JupiterStateModel): string {
    return state.relatedToKey;
  }

  @Selector()
  static getMainObjectLabel(state: JupiterStateModel): string {
    return state.context?.relatedToObject?.label ?? state.relatedToObject?.label;
  }

  @Selector()
  static getBackUrl(state: JupiterStateModel): string {
    return state.backUrl;
  }

  @Selector()
  static getRelatedTo(state: JupiterStateModel): string {
    return state.relatedToObject.name;
  }

  @Selector()
  static getRelatedToObject(state: JupiterStateModel): CustomObject {
    return state.relatedToObject;
  }

  @Selector()
  static getModuleBaseUrl(state: JupiterStateModel): string {
    return state.relatedToObject?.name?.includes('co_') ? RelatedToType.CustomObjectValue : state.relatedToObject?.name;
  }

  @Selector()
  static getViews(state: JupiterStateModel): SavedView[] {
    return state.views;
  }

  @Selector()
  static getView(state: JupiterStateModel): SavedView {
    return state.view;
  }

  @Selector()
  static getScheduledExports(state: JupiterStateModel): ExportScheduleForm[] {
    return state.scheduledExports;
  }

  @Selector()
  static getActiveExportScheduleId(state: JupiterStateModel): number | null {
    return state.activeExportScheduleId;
  }

  @Selector([JupiterState.getScheduledExports, JupiterState.getActiveExportScheduleId])
  static getActiveExportSchedule(
    _: JupiterStateModel,
    scheduledExports: ExportScheduleForm[],
    activeExportScheduleId: number | null,
  ): ExportScheduleForm | null {
    return scheduledExports.find(({ id }) => id === activeExportScheduleId) ?? null;
  }

  @Selector()
  static getContext(state: JupiterStateModel): RelatedToActions.RelatedToContext & {
    service: JupiterService;
    relatedToObject: CustomObject;
  } {
    return state.context;
  }

  @Selector()
  static getFeatureSupport(state: JupiterStateModel): JupiterStateFeatureSupportModel {
    return state.featureSupport;
  }

  @Selector()
  static getContextFeatureSupport(state: JupiterStateModel): JupiterStateFeatureSupportModel {
    return state.context?.featureSupport;
  }

  @Selector()
  static getEngagements(state: JupiterStateModel): { items: Engagement[]; contacts?: Contact[] } {
    return state.engagements;
  }

  @Selector()
  static getEngagementsItems(state: JupiterStateModel): Engagement[] {
    return state.engagements.items;
  }

  @Selector()
  static getEngagementsContacts(state: JupiterStateModel): Contact[] {
    return state.engagements.contacts;
  }

  @Selector()
  static getPropertyUnitsItems(state: JupiterStateModel): PropertyUnit[] {
    return state.propertyUnits.items;
  }

  @Selector()
  static getTransient(state: JupiterStateModel): Dictionary<any> {
    return state.transient;
  }

  @Selector()
  static getDealsItems(state: JupiterStateModel): Project[] {
    return state.deals.items;
  }

  @Selector()
  static getPropertiesItems(state: JupiterStateModel): Property[] {
    return state.properties.items;
  }

  @Selector()
  static getLoanQuotesItems(state: JupiterStateModel): LoanQuote[] {
    return state.loanQuotes.items;
  }

  @Selector()
  static getContactsItems(state: JupiterStateModel): Contact[] {
    return state.contacts.items;
  }

  @Selector()
  static getTaskTabPrefix(state: JupiterStateModel): string {
    if (state.isCustom) {
      return 'CustomObjectValue';
    }

    return state.relatedToObject.name;
  }

  @Selector()
  static getRelationEntityValue(state: JupiterStateModel): any {
    if (state.relatedToObject.uiConfig.isForStandardRelation) {
      return {};
    }

    return { [state.context.relationFieldName]: state.context.relationValue };
  }

  @Selector()
  static getIsBusy(state: JupiterStateModel): boolean {
    return state.global.isBusy;
  }

  @Selector()
  static getIsSearching(state: JupiterStateModel): boolean {
    return state.global.isSearching;
  }

  static getAddFormDefinition(): any {
    return createSelector(
      [JupiterState, AccountState, ObjectsManagerState],
      (
        jupiterState: JupiterStateModel,
        accountState: AccountStateModel,
        objectsManagerState: ObjectsManagerStateModel,
      ) => {
        const filterPropertyAddFormFields = (
          fieldsToFilter:
            | { name: string; isRequired?: boolean; isRemovable?: boolean }[]
            | { name: string; isRequired?: boolean; isRemovable?: boolean }[][],
        ) => {
          const isPropertyContext = jupiterState.relatedToObject.name === RelatedToType.Property;
          if (!isPropertyContext) return fieldsToFilter;

          const allowedFields = ObjectsManagerState.getFieldsByObjectName(RelatedToType.Property, {
            allowedFieldsOnly: true,
            addNestedFields: false,
          })(objectsManagerState, ObjectsManagerState.getMappedObjects(objectsManagerState));

          const isAllowed = (field: { name: string }) =>
            (['template_id', 'cff_address'].includes(field.name) || allowedFields.some((f) => f.name === field.name)) &&
            !PROPERTY_ADD_FORM_FIELDS_BLACKLIST.includes(field.name);

          const items = fieldsToFilter ?? [];
          if (items.length > 0 && Array.isArray(items[0])) {
            return (items as { name: string }[][]).map((row) => row.filter(isAllowed)).filter((row) => row.length > 0);
          }

          return (items as { name: string }[]).filter(isAllowed);
        };

        let fields = null;
        const persistedInSavedView = isAddFormDefinitionPersisted(jupiterState);

        if (persistedInSavedView) {
          const definition = JSON.parse(jupiterState.view.definition);
          const addFormDefinitionKey = jupiterState.isCustom ? 'custom_object_form_definition' : 'add_form_definition';
          const formDefinition = definition[addFormDefinitionKey] ?? '{}';
          const typesPersistedAsObject = [RelatedToType.Property, RelatedToType.Company, RelatedToType.Contact];

          if (typesPersistedAsObject.includes(jupiterState.relatedToObject.name as RelatedToType)) {
            fields = formDefinition?.fields;

            if (!fields) {
              switch (jupiterState.relatedToObject.name) {
                case RelatedToType.Property:
                  fields = defaultPropertyAddFormDefinition.fields;
                  break;
              }
            }
          } else {
            fields = JSON.parse(formDefinition)?.fields;
          }
        } else {
          fields = AccountState.getDefinitions(accountState)?.[jupiterState.relatedToObject.name.toLowerCase()]?.fields;
        }

        if (fields?.length) {
          return filterPropertyAddFormFields(fields);
        }

        const defaultFields = ObjectsManagerState.getFieldsByObjectName(jupiterState.relatedToObject.name, {
          allowedFieldsOnly: true,
          addNestedFields: false,
        })(objectsManagerState, ObjectsManagerState.getMappedObjects(objectsManagerState)).map((field) => ({
          name: field.name,
          isRequired: false,
          isRemovable: false,
        }));

        return filterPropertyAddFormFields(defaultFields);
      },
    );
  }

  @Action(RelatedToActions.Set)
  setRelatedTo(
    stateContext: StateContext<JupiterStateModel>,
    { relatedToKey, context }: { relatedToKey; context: RelatedToActions.RelatedToContext },
  ) {
    const tsObject = this.store.selectSnapshot(ObjectsManagerState.getByUrlPrefix(relatedToKey));
    const account = this.store.selectSnapshot(AccountState.getAccount);

    this.jupiterDataService.accountId = Number(account.id);
    this.service = new JupiterService(this.jupiterDataService, tsObject);

    let contextInfo = null;

    if (context) {
      const contextRelatedToObject = this.store.selectSnapshot(
        ObjectsManagerState.getByUrlPrefix(context.relatedToKey),
      );
      const relationField = this.getRelationFieldName(tsObject, contextRelatedToObject, context.standardRelation);
      contextInfo = {
        ...context,
        service: new JupiterService(this.jupiterDataService, contextRelatedToObject),
        relatedToObject: contextRelatedToObject,
        relationFieldName: relationField?.name,
        featureSupport: getFeatureSupport(
          contextRelatedToObject,
          this.store.selectSnapshot(AccountState.getFeatureFlags),
        ),
        relationValue: this.getRelationValue(
          tsObject,
          contextRelatedToObject,
          stateContext.getState().entity,
          context.standardRelation,
        ),
      };
    }

    let newState: Partial<JupiterStateModel> = {
      relatedToKey,
      isCustom: !!tsObject.id,
      relatedToObject: tsObject,
      transient: {},
      context: contextInfo,
      slots: {
        labels: getLabelSlots(tsObject),
        permissions: this.getPermissionSlots(tsObject.name, this.store.selectSnapshot(ObjectsManagerState.get)),
      },
      backUrl: stateContext.getState().backUrl ?? null,
    };

    newState.featureSupport = getFeatureSupport(tsObject, this.store.selectSnapshot(AccountState.getFeatureFlags));

    stateContext.patchState(newState);
  }

  @Action(RelatedToActions.SetBackUrl)
  setBackUrl(stateContext: StateContext<JupiterStateModel>, { backUrl }: { backUrl: string }) {
    stateContext.patchState({ backUrl });
  }

  @Action(ViewActions.SetViews)
  setViews(context: StateContext<JupiterStateModel>) {
    const recordContext = context.getState().context;
    const parentObjectType = recordContext?.relatedToId != null ? recordContext.relatedToObject?.name : undefined;

    return this.service.getViews(parentObjectType).pipe(tap((views) => context.patchState({ views })));
  }

  @Action(ViewActions.SetView)
  setView(context: StateContext<JupiterStateModel>, { viewId }: { viewId }) {
    context.patchState({ view: { ...context.getState().views.find(({ id }) => id === Number(viewId)) } });
  }

  @Action(ViewActions.ClearView)
  clearView(context: StateContext<JupiterStateModel>, { viewId }: { viewId }) {
    context.patchState({ view: null });
  }

  @Action(ExportScheduleActions.SetList)
  setExportSchedules(
    context: StateContext<JupiterStateModel>,
    {
      scheduledExports,
    }: {
      scheduledExports: ExportScheduleForm[];
    },
  ) {
    const state = context.getState();
    const activeExportScheduleId = scheduledExports.some(({ id }) => id === state.activeExportScheduleId)
      ? state.activeExportScheduleId
      : null;

    context.patchState({
      scheduledExports,
      activeExportScheduleId,
    });
  }

  @Action(ExportScheduleActions.SetActive)
  setActiveExportSchedule(context: StateContext<JupiterStateModel>, { id }: { id: number | null }) {
    context.patchState({ activeExportScheduleId: id });
  }

  @Action(ExportScheduleActions.Reset)
  resetExportSchedules(context: StateContext<JupiterStateModel>) {
    context.patchState({
      scheduledExports: [],
      activeExportScheduleId: null,
    });
  }

  @Action(ExportScheduleActions.List)
  listExportSchedules(context: StateContext<JupiterStateModel>) {
    const viewId = context.getState().view?.id;
    if (!viewId) {
      return EMPTY;
    }

    return this.jupiterDataService.exportScheduleService.list(viewId).pipe(
      tap((scheduledExports) => {
        context.dispatch(new ExportScheduleActions.SetList(scheduledExports));
      }),
    );
  }

  @Action(ExportScheduleActions.Create)
  createExportSchedule(context: StateContext<JupiterStateModel>, { schedule }: { schedule: ExportScheduleForm }) {
    const viewId = context.getState().view?.id;
    if (!viewId) {
      return EMPTY;
    }

    return this.jupiterDataService.exportScheduleService.create(viewId, schedule).pipe(
      tap((createdSchedule) => {
        const state = context.getState();
        const scheduledExports = [...state.scheduledExports, createdSchedule];

        context.patchState({
          scheduledExports,
          activeExportScheduleId: createdSchedule?.id ?? state.activeExportScheduleId,
        });
      }),
    );
  }

  @Action(ExportScheduleActions.Update)
  updateExportSchedule(context: StateContext<JupiterStateModel>, { schedule }: { schedule: ExportScheduleForm }) {
    const viewId = context.getState().view?.id;
    if (!viewId) {
      return EMPTY;
    }

    return this.jupiterDataService.exportScheduleService.update(viewId, schedule).pipe(
      tap((updatedSchedule) => {
        const state = context.getState();
        const scheduleId = updatedSchedule.id ?? schedule.id;
        const scheduledExports = state.scheduledExports.map((item) =>
          item.id === scheduleId ? { ...item, ...updatedSchedule, id: scheduleId } : item,
        );

        context.patchState({ scheduledExports });
      }),
    );
  }

  @Action(ExportScheduleActions.Delete)
  deleteExportSchedule(context: StateContext<JupiterStateModel>, { id }: { id: number }) {
    const viewId = context.getState().view?.id;
    if (!viewId) {
      return EMPTY;
    }

    return this.jupiterDataService.exportScheduleService.delete(viewId, id).pipe(
      tap(() => {
        const state = context.getState();
        const scheduledExports = state.scheduledExports.filter((item) => item.id !== id);
        const activeExportScheduleId = state.activeExportScheduleId === id ? null : state.activeExportScheduleId;

        context.patchState({
          scheduledExports,
          activeExportScheduleId,
        });
      }),
    );
  }

  private getContextualizedFjson(
    stateContext: StateContext<JupiterStateModel>,
    fjson: string,
    ignoreRelation: boolean,
  ): string {
    const context = stateContext.getState().context;

    if (context && !ignoreRelation) {
      let fjsonContextFilter: [string, string, any];

      // if (context.standardRelation?.fieldName) {
      //   fjsonContextFilter = [context.standardRelation.fieldName, 'in', [context.relatedToId]];
      // } else {
      fjsonContextFilter = getLinkedFieldAsFjson(
        this.store,
        stateContext.getState().relatedToObject.name,
        context.relatedToObject.name as string,
        context.relatedToId,
      );
      // }

      let parsedFjson = JSON.parse(fjson ?? '[]');
      parsedFjson = [...parsedFjson, ...fjsonContextFilter];
      return JSON.stringify(parsedFjson);
    }

    return fjson;
  }

  @Action(EntityActions.Search, { cancelUncompleted: true })
  search(stateContext: StateContext<JupiterStateModel>, { params }: { params: GraphqlParams }) {
    if (params.connection === RelatedToType.Property) {
      params.connection = GraphqlConnection.properties;

      params.options = { ...(params.options ?? {}), keepConnectionCase: true };
    }

    if (params.connection === RelatedToType.Project) {
      params.connection = GraphqlConnection.projects;
      params.options = { ...(params.options ?? {}), keepConnectionCase: true };
      params.filter = { ...params.filter, pipeline_id: stateContext.getState().view.id };

      if (stateContext.getState().relatedToObject.uiConfig.urlPrefix === 'property-deals') {
        delete params.filter.pipeline_id;
      }
    }

    stateContext.patchState({ global: { ...stateContext.getState().global, isSearching: true } });

    let source = of({} as Entity);

    const hasStandardRelation = !!stateContext.getState().context?.standardRelation;
    let fjson = this.getContextualizedFjson(stateContext, params.filter.fjson, hasStandardRelation);

    if (hasStandardRelation) {
      if (this.isStandardRelationBetween(stateContext, RelatedToType.Property, RelatedToType.Property)) {
        source = source.pipe(
          switchMap(() =>
            this.service.loadEntity(stateContext.getState().context.relatedToId, ['latitude', 'longitude']),
          ),
          tap((entity) => {
            params.filter.location = {
              near: {
                lat: Number(entity.latitude),
                lon: Number(entity.longitude),
              },
              within: '1000000mi',
            };
          }),
        );
      } else if (this.isStandardRelationBetween(stateContext, RelatedToType.Property, RelatedToType.Project)) {
        source = source.pipe(
          switchMap(() =>
            this.service.loadEntityByType(
              stateContext.getState().context.relatedToId,
              stateContext.getState().context.relatedToObject,
              ['project_ids'],
            ),
          ),
          tap((entity) => {
            const fjsonArr = JSON.parse(fjson ?? '[]');
            fjsonArr.push(['id', 'in', (entity as any).project_ids]);
            fjson = JSON.stringify(fjsonArr);
            params.fields = this.store
              .selectSnapshot(
                ObjectsManagerState.getFieldsByObjectName(stateContext.getState().relatedToObject.name, {
                  addNestedFields: true,
                  allowedFieldsOnly: true,
                }),
              )
              .filter((x) => params.fields?.includes(x.name))
              .map((x) => x.name);
            params.fields.push('pipeline_id');
          }),
        );
      } else if (this.isStandardRelationBetween(stateContext, RelatedToType.Project, RelatedToType.Contact)) {
        if (stateContext.getState().context.standardRelation.asFjson) {
          const fjsonArr = JSON.parse(fjson ?? '[]');
          fjsonArr.push([
            stateContext.getState().context.standardRelation.fieldName,
            'in',
            [stateContext.getState().context.relatedToId],
          ]);
          fjson = JSON.stringify(fjsonArr);
        } else {
          throw new Error('Not implemented');
        }
      } else {
        params.filter[stateContext.getState().context.standardRelation.fieldName] =
          stateContext.getState().context.relatedToId;
      }
    }

    if (params.filter?.queryObject?.groupBy?.length) {
      params.groupBy = params.filter?.queryObject?.groupBy?.map((groupBy) => ({
        field: groupBy,
        order: params.filter.sort[0]?.order ?? 'asc',
      }));
      delete params.filter.queryObject.groupBy;
    }

    return source.pipe(
      switchMap(() => {
        return this.service
          .search<GraphqlResultInfo<any>>({
            ...params,
            filter: {
              ...params.filter,
              fjson,
            },
            fieldDefs: this.store.selectSnapshot(
              ObjectsManagerState.getFieldsByObjectName(
                stateContext.getState().relatedToObject.name,
                {
                  addNestedFields: true,
                  allowedFieldsOnly: true,
                },
                this.store,
              ),
            ),
          })
          .pipe(
            tap((result: GraphqlResultInfo<any>) => {
              let rows = [];
              let data: TableData<any> = { rows: [], pageInfo: DEFAULT_PAGE_INFO };

              if (params.groupBy) {
                //@ts-ignore
                rows = mapGroupBySearchResultsToRows(result.edges, params.fields);
                rows = addGroupAggregations(rows);
                data = { rows, pageInfo: result.pageInfo };
              } else {
                data = { rows: result.edges.map((edge) => edge.node), pageInfo: result.pageInfo };
              }

              const requestedPage = params.filter?.pagination?.page ?? 1;
              const shouldAppend =
                Boolean((params.options as any)?.append) && requestedPage > 1 && stateContext.getState().data.rows;
              const nextRows = shouldAppend ? [...stateContext.getState().data.rows, ...data.rows] : data.rows;

              stateContext.patchState({
                data: {
                  rows: nextRows,
                  rowsMemento: cloneDeep(nextRows),
                  pageInfo: data.pageInfo,
                },
                global: { ...stateContext.getState().global, isSearching: false },
              });
            }),
          );
      }),
    );
  }

  @Action(EntityActions.UpdateRowsMemento)
  updateRowsMemento(stateContext: StateContext<JupiterStateModel>, { rows }: { rows: any[] }) {
    stateContext.patchState({ data: { ...stateContext.getState().data, rows, rowsMemento: cloneDeep(rows) } });
  }

  @Action(EntityActions.Export)
  export(
    context: StateContext<JupiterStateModel>,
    { params, exportOptions }: { params: GraphqlParams; exportOptions: ExportOptions },
  ) {
    const hasStandardRelation = !!context.getState().context?.standardRelation;
    const fjson = this.getContextualizedFjson(context, params.filter.fjson, hasStandardRelation);

    if (hasStandardRelation) {
      params.filter[context.getState().context.standardRelation.fieldName] = context.getState().context.relatedToId;
    }

    if (params.connection === RelatedToType.Property) {
      params.connection = GraphqlConnection.properties;
    }

    if (params.connection === RelatedToType.Project) {
      params.connection = GraphqlConnection.projects;
    }

    params.options = { ...(params.options ?? {}), keepConnectionCase: true };

    return this.service
      .search<any>(
        {
          ...params,
          filter: {
            ...params.filter,
            fjson,
          },
          fieldDefs: this.store.selectSnapshot(
            ObjectsManagerState.getFieldsByObjectName(context.getState().relatedToObject.name, {
              addNestedFields: true,
              allowedFieldsOnly: true,
            }),
          ),
        },
        exportOptions,
      )
      .pipe(
        tap((data: any) => {
          if (data?.message) {
            let snackbar = this.tsSnackbarService.open(data.message, 'info', 'View exports');
            snackbar.onAction().subscribe(() => {
              this.router.navigate(['/settings/jobs-status']);
            });
          } else {
            generateDownloadableFile(data, FileFormat.CSV, exportOptions.fileName);
          }
        }),
      );
  }

  @Action(EntityActions.SetEntity)
  setEntity(stateContext: StateContext<JupiterStateModel>, { entity }: { entity }) {
    stateContext.patchState({ entity });
  }

  @Action(EntityActions.Load)
  loadEntity(stateContext: StateContext<JupiterStateModel>, { id }: { id }) {
    const context = stateContext.getState().context;

    stateContext.patchState({ global: { ...stateContext.getState().global, isBusy: true } });

    return (context ? context.service : this.service).loadEntity(id).pipe(
      tap((entity) => {
        let newState: JupiterStateModel = {
          ...stateContext.getState(),
          entity,
          global: { ...stateContext.getState().global, isBusy: false },
        };

        if (context && !context.relationValue) {
          newState = {
            ...newState,
            context: {
              ...newState.context,
              relationValue: this.getRelationValue(newState.relatedToObject, newState.context.relatedToObject, entity),
            },
          };
        }

        stateContext.patchState(newState);
      }),
    );
  }

  @Action(EntityActions.LoadTransient)
  loadTransientEntity(stateContext: StateContext<JupiterStateModel>, { id }: { id }) {
    return this.service.loadEntity(id).pipe(tap((entity) => stateContext.patchState({ transientEntity: entity })));
  }

  @Action(EntityActions.Update)
  updateEntity(stateContext: StateContext<JupiterStateModel>, { entity, type }: { entity; type?: CustomObject }) {
    const context = stateContext.getState().context;
    stateContext.patchState({ global: { ...stateContext.getState().global, isBusy: true } });

    const newState: any = {
      global: { ...stateContext.getState().global, isBusy: false },
    };

    return (type ? this.service.updateEntityByType(entity, type) : this.service.updateEntity(entity)).pipe(
      switchMap((data) => {
        if ([RelatedToType.Property].includes(stateContext.getState().relatedToObject.name as RelatedToType)) {
          return this.service.loadEntity(entity.id).pipe(
            tap((response) => {
              if (!context) {
                newState.entity = response;
              }

              stateContext.patchState(newState);
            }),
          );
        }

        if (!context) {
          newState.entity = data;
        }

        stateContext.patchState(newState);

        return of();
      }),
    );
  }

  @Action(EntityActions.UpdateEntityTemplate)
  updateEntityTemplate(context: StateContext<JupiterStateModel>, { templateId }: { templateId }) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    return this.service.updateEntityTemplate(context.getState().entity.id, templateId).pipe(
      tap((response) => {
        context.patchState({
          entity: response,
          global: { ...context.getState().global, isBusy: false },
        });
      }),
    );
  }

  @Action(EntityActions.ClearTransient)
  clearTransientEntity(context: StateContext<JupiterStateModel>) {
    context.patchState({ transientEntity: null });
  }

  private isStandardRelationBetween(
    context: StateContext<JupiterStateModel>,
    mainObject: RelatedToType,
    relatedObject: RelatedToType,
  ): boolean {
    if (!context.getState().relatedToObject.uiConfig.isForStandardRelation) {
      return false;
    }

    return (
      context.getState().context.relatedToObject.name === mainObject &&
      context.getState().relatedToObject.name === relatedObject
    );
  }

  @Action(EntityActions.Add)
  addEntity(context: StateContext<JupiterStateModel>, { entity }: { entity }) {
    return this.service.addEntity(entity).pipe(
      switchMap((data) => {
        if (this.isStandardRelationBetween(context, RelatedToType.Project, RelatedToType.Contact)) {
          return this.service
            .createContactAssociation({
              contact: data,
              entityId: context.getState().entity.id,
              relatedToType: RelatedToType.Project,
            })
            .pipe(switchMap(() => of(data)));
        }

        return of(data);
      }),
      tap((data) => {
        if (!context.getState().context) {
          context.patchState({ entity: data });
        }
      }),
    );
  }

  @Action(EntityActions.Duplicate)
  duplicateEntity(
    context: StateContext<JupiterStateModel>,
    { entityId, origin }: { entityId?: number; origin: 'header' | 'table' },
  ) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    const stateContext = context.getState();
    const service = origin === 'header' && stateContext.context ? stateContext.context.service : this.service;

    return service.duplicateEntity(entityId ?? stateContext.entity.id).pipe(
      tap((data) => {
        context.patchState({ transientEntity: data, global: { ...context.getState().global, isBusy: false } });
      }),
      catchError((error) => {
        context.patchState({ global: { ...context.getState().global, isBusy: false } });
        return throwError(() => error);
      }),
    );
  }

  @Action(EntityActions.BulkUpdate)
  bulkUpdate(context: StateContext<JupiterStateModel>, { params }: { params: BulkUpdateParams }) {
    // this is not implemented yet, but we will once all objects are in Jupiter
    throw new Error('Not implemented');
  }

  @Action(EntityActions.GenerateMergeDocument)
  generateMergeDocument(
    context: StateContext<JupiterStateModel>,
    { data }: { data: { fileResourceId: number; format: string; sendToDocusign: boolean } },
  ) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    return this.service
      .generateMergeDocument({
        fileResourceId: data.fileResourceId,
        format: data.format,
        sendToDocusign: data.sendToDocusign,
        entityId: context.getState().entity.id,
        accountId: Number(this.store.selectSnapshot(AccountState.getAccount).id),
      })
      .pipe(
        tap((response) => {
          if (response?.data) {
            if (response.url) {
              window.open(response.url, '_blank');
            } else if ([FileFormat.PDF, FileFormat.DOCX, FileFormat.PPTX].includes(data.format as FileFormat))
              generateDownloadableFile(
                response.data,
                data.format as FileFormat,
                new shortUniqueId({ length: 6, dictionary: 'number' }).rnd().toLowerCase(),
              );
          }

          context.patchState({ global: { ...context.getState().global, isBusy: false } });
        }),
      );
  }

  @Action(EntityActions.Delete)
  deleteEntity(context: StateContext<JupiterStateModel>, { id, forceDelete }: { id: number; forceDelete: boolean }) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    return this.service.deleteEntity(id, forceDelete).pipe(
      tap(() => context.patchState({ global: { ...context.getState().global, isBusy: false } })),
      catchError((error) => {
        context.patchState({ global: { ...context.getState().global, isBusy: false } });
        return throwError(() => error);
      }),
    );
  }

  @Action(EntityActions.Clear)
  clearEntity(context: StateContext<JupiterStateModel>) {
    context.patchState({ entity: null, propertyUnits: { items: [] } });
  }

  @Action(EntityActions.ClearData)
  clearData(context: StateContext<JupiterStateModel>) {
    context.patchState({ data: { rows: [], pageInfo: DEFAULT_PAGE_INFO }, view: null, views: [] });
  }

  @Action(EntityActions.UpdateAvatar)
  updateAvatar(context: StateContext<JupiterStateModel>, { file }: { file: File }) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    return this.service.updateAvatar(context.getState().entity.id, file).pipe(
      tap((response) => {
        context.patchState({ entity: response, global: { ...context.getState().global, isBusy: false } });
      }),
    );
  }

  @Action(EntityActions.PatchEntity)
  patchEntity(context: StateContext<JupiterStateModel>, { entity }: { entity: Partial<Entity> }) {
    context.patchState({ entity: { ...context.getState().entity, ...entity } });
  }

  @Action(EntityActions.DeleteAvatar)
  deleteAvatar(context: StateContext<JupiterStateModel>) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    return this.service.deleteAvatar(context.getState().entity.id).pipe(
      tap((response) => {
        context.patchState({
          entity: { ...response, avatar_url: null },
          global: { ...context.getState().global, isBusy: false },
        });
      }),
    );
  }

  @Action(RelatedToActions.Clear)
  clearRelatedTo(context: StateContext<JupiterStateModel>) {
    context.patchState({ ...defaultStateValue });
  }

  @Action(TemplateActions.Create)
  createTemplate(context: StateContext<JupiterStateModel>, { template }: { template }) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    return this.service.createTemplate(template).pipe(
      switchMap((response) => {
        return this.service.updateEntityTemplate(context.getState().entity.id, response.id).pipe(
          tap(() => {
            context.patchState({
              entity: { ...context.getState().entity, template: response, template_id: response.id },
              global: { ...context.getState().global, isBusy: false },
            });
          }),
        );
      }),
    );
  }

  @Action(TemplateActions.Update)
  updateTemplate(context: StateContext<JupiterStateModel>, { template, silently }: { template; silently }) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    return this.service.updateTemplate(template).pipe(
      tap((response) => {
        if (!silently) {
          context.patchState({
            entity: { ...context.getState().entity, template: response },
            global: { ...context.getState().global, isBusy: false },
          });
        } else {
          context.patchState({
            global: { ...context.getState().global, isBusy: false },
          });
        }
      }),
    );
  }

  @Action(TemplateActions.ToggleSummary)
  toggleSummary(context: StateContext<JupiterStateModel>, { value }: { value: boolean }) {}

  @Action(EntityActions.ImageActions.Add)
  addImage(context: StateContext<JupiterStateModel>, { entity, image }: { entity; image }) {
    context.patchState({ global: { ...context.getState().global, isBusy: true } });

    return this.service.uploadImage(entity.id, image).pipe(
      tap((response) => {
        context.patchState({
          entity: { ...context.getState().entity, images: [...context.getState().entity.images, response] },
          global: { ...context.getState().global, isBusy: false },
        });
      }),
    );
  }

  @Action(EntityActions.ImageActions.Delete)
  deleteImage(context: StateContext<JupiterStateModel>, { id }: { id }) {
    return this.service.deleteImage(id).pipe(
      tap((response) => {
        context.patchState({
          entity: { ...context.getState().entity, images: context.getState().entity.images.filter((x) => x.id !== id) },
        });
      }),
    );
  }

  @Action(ViewActions.AddView)
  addView(context: StateContext<JupiterStateModel>, { view }: { view }) {
    const recordContext = context.getState().context;
    const payload =
      recordContext?.relatedToId != null && !view.parent_object_type
        ? {
            ...view,
            parent_object_type: recordContext.relatedToObject?.name,
          }
        : view;

    return this.service.addView(payload).pipe(
      tap((savedView) => {
        context.patchState({
          views: [...context.getState().views, savedView],
        });
      }),
    );
  }

  @Action(ViewActions.UpdateView)
  updateView(
    context: StateContext<JupiterStateModel>,
    { viewDefinition, filterDefinition }: { viewDefinition; filterDefinition },
  ) {
    const view = { ...context.getState().view };
    view.definition = viewDefinition;
    view.saved_filter.filters = filterDefinition;
    return this.service.updateView(view).pipe(
      tap((savedView) => {
        context.patchState({
          view: savedView,
          views: context.getState().views.map((v) => (v.id === savedView.id ? savedView : v)),
        });
      }),
    );
  }

  @Action(ViewActions.UpdateViewName)
  updateViewName(context: StateContext<JupiterStateModel>, { name }: { name }) {
    const view = { ...context.getState().view };
    return this.service.updateView({ id: view.id, name }, true).pipe(
      tap((savedView) => {
        context.patchState({
          view: savedView,
          views: context.getState().views.map((v) => (v.id === savedView.id ? savedView : v)),
        });
      }),
    );
  }

  @Action(ViewActions.DuplicateView)
  duplicateView(context: StateContext<JupiterStateModel>) {
    return this.service.getView(context.getState().view.id).pipe(
      switchMap((savedView) => {
        const recordContext = context.getState().context;
        const payload = {
          ...savedView,
          name: `${savedView.name} Copy`,
          id: null,
          ...(recordContext?.relatedToId != null ? { parent_object_type: recordContext.relatedToObject?.name } : {}),
        };

        return this.service.addView(payload).pipe(
          tap((response) => {
            context.patchState({
              view: response,
              views: [...context.getState().views, response],
            });
          }),
        );
      }),
      catchError((err) => {
        this.tsSnackbarService.open(err?.error?.error || 'Unable to duplicate view', 'error');
        return EMPTY;
      }),
    );
  }

  @Action(ViewActions.DeleteView)
  deleteView(context: StateContext<JupiterStateModel>) {
    const id = context.getState().view.id;
    return this.service.deleteView(id).pipe(
      tap(() => {
        const [firstView] = context.getState().views;
        context.patchState({
          view: firstView,
          views: context.getState().views.filter((view) => view.id !== id),
        });
      }),
    );
  }

  @Action(TemplateActions.UpdateAddForm)
  updateAddForm(context: StateContext<JupiterStateModel>, { fields }: { fields }) {
    const persistInSavedView = isAddFormDefinitionPersisted(context.getState());

    if (persistInSavedView) {
      const definition = JSON.parse(context.getState().view.definition);
      const addFormDefinitionKey = context.getState().isCustom
        ? 'custom_object_form_definition'
        : 'add_form_definition';

      definition[addFormDefinitionKey] = context.getState().isCustom ? JSON.stringify({ fields }) : { fields };

      return this.service.updateView({ ...context.getState().view, definition: JSON.stringify(definition) }).pipe(
        tap((response) => {
          context.patchState({
            view: response,
            views: context.getState().views.map((v) => (v.id === response.id ? response : v)),
          });
        }),
      );
    } else {
      const account = this.store.selectSnapshot(AccountState.getAccount);
      const definitions = account.definitions;
      if (!definitions[context.getState().relatedToObject.name.toLowerCase()]) {
        definitions[context.getState().relatedToObject.name.toLowerCase()] = { fields: [] };
      }

      definitions[context.getState().relatedToObject.name.toLowerCase()].fields = fields;

      return this.store.dispatch(
        new AccountActions.UpdateAccount({
          id: account.id,
          configuration: { definitions: JSON.stringify(definitions) },
        }),
      );
    }
  }

  @Action(EntityActions.EngagementActions.Load)
  loadEngagements(context: StateContext<JupiterStateModel>, { mode }: { mode: 'onlyEngagements' | 'all' }) {
    const observables = [this.service.getEngagements(context.getState().entity.id)];

    if (context.getState().relatedToObject.name === RelatedToType.Company && mode === 'all') {
      observables.push(
        this.service.getRelatedContacts({
          entityId: context.getState().entity.id,
          fields: ['id', 'name'],
        }),
      );
    } else {
      observables.push(of([]));
    }

    return forkJoin(observables).pipe(
      tap(([engagements, contacts]) => {
        context.patchState({ engagements: { items: engagements, contacts } });
      }),
    );
  }

  @Action(EntityActions.EngagementActions.Delete)
  deleteEngagement(context: StateContext<JupiterStateModel>, { id }: { id: number }) {
    return this.service.deleteEngagement(id);
  }

  @Action(EntityActions.DealActions.Load)
  loadDeals(context: StateContext<JupiterStateModel>, { fields }: { fields: string[] }) {
    return this.service.getRelatedDeals({ entityId: context.getState().entity.id, fields }).pipe(
      tap((deals) => {
        context.patchState({ deals: { items: deals } });
      }),
    );
  }

  @Action(EntityActions.DealActions.Create)
  createDeal(context: StateContext<JupiterStateModel>, { model }: { model: any }) {
    context.patchState({
      deals: {
        ...context.getState().deals,
        items: [...context.getState().deals.items, model],
      },
    });
  }

  @Action(EntityActions.PropertyActions.Load)
  loadProperties(context: StateContext<JupiterStateModel>, { fields }: { fields: string[] }) {
    return this.service.getRelatedProperties({ entityId: context.getState().entity.id, fields }).pipe(
      tap((properties) => {
        context.patchState({ properties: { items: properties } });
      }),
    );
  }

  @Action(EntityActions.PropertyActions.Create)
  createProperty(context: StateContext<JupiterStateModel>, { model }: { model: any }) {
    context.patchState({
      properties: {
        ...context.getState().properties,
        items: [...context.getState().properties.items, model],
      },
    });
  }

  @Action(EntityActions.PropertyActions.UpdateWithGeocode)
  updatePropertyWithGeocode(
    stateContext: StateContext<JupiterStateModel>,
    {
      property,
      originalProperty,
      updateGeoCode,
    }: { property: Property; originalProperty?: Property; updateGeoCode?: boolean },
  ) {
    stateContext.patchState({ global: { ...stateContext.getState().global, isBusy: true } });

    const newState: any = {
      global: { ...stateContext.getState().global, isBusy: false },
      data: { ...stateContext.getState().data, rows: [...stateContext.getState().data.rows] },
    };

    // If updateGeoCode is not provided, show the confirmation dialog
    const updateGeoCode$ =
      updateGeoCode !== undefined
        ? of(updateGeoCode)
        : this.confirmationDialogService
            .openConfirmationDialog(ConfirmationDialogService.updateGeoCodeCommonLayoutConfig())
            .pipe(
              catchError(() => {
                // If dialog is cancelled, default to false (skip geocode update)
                return of(undefined);
              }),
            );

    return updateGeoCode$.pipe(
      filter((remapLocation): remapLocation is boolean => remapLocation !== undefined),
      switchMap((shouldUpdateGeoCode: boolean) => {
        if (shouldUpdateGeoCode) {
          // Update with geocode
          return this.jupiterDataService.propertiesService.saveProperty(property, originalProperty || null, true).pipe(
            switchMap(() => {
              // Reload the entity to get updated data
              return this.service.loadEntity(property.id);
            }),
            tap((response) => {
              stateContext.patchState(this.updateEntityData(newState, stateContext, response));
            }),
          );
        } else {
          // User chose to skip geocode update, process as normal update
          return this.service.updateEntity(property).pipe(
            switchMap((data) => {
              if (
                [RelatedToType.PropertyTenant, RelatedToType.Property].includes(
                  stateContext.getState().relatedToObject.name as RelatedToType,
                )
              ) {
                return this.service.loadEntity(property.id).pipe(
                  tap((response) => {
                    stateContext.patchState(this.updateEntityData(newState, stateContext, response));
                  }),
                );
              }

              stateContext.patchState(this.updateEntityData(newState, stateContext, data));

              return of();
            }),
          );
        }
      }),
    );
  }

  @Action(EntityActions.ContactActions.Load)
  loadContacts(context: StateContext<JupiterStateModel>, { fields }: { fields: string[] }) {
    return this.service.getRelatedContacts({ entityId: context.getState().entity.id, fields }).pipe(
      tap((contacts) => {
        context.patchState({ contacts: { items: contacts } });
      }),
    );
  }

  @Action(EntityActions.ContactActions.Create)
  createContact(context: StateContext<JupiterStateModel>, { model }: { model: any }) {
    const patchState = () => {
      context.patchState({
        contacts: {
          ...context.getState().contacts,
          items: [...context.getState().contacts.items, model],
        },
      });
    };

    if (context.getState().relatedToObject.name === RelatedToType.Property) {
      return this.service
        .createContactAssociation({
          contact: model,
          entityId: context.getState().entity.id,
          relatedToType: RelatedToType.Property,
        })
        .pipe(tap(() => patchState()));
    }

    patchState();
  }

  @Action(EntityActions.ContactActions.DeleteContactAssociation)
  deleteContactAssociation(context: StateContext<JupiterStateModel>, { associationId }: { associationId: number }) {
    if (context.getState().relatedToObject.name === RelatedToType.Property) {
      return this.service.deleteContactAssociation({
        associationId,
        entityId: context.getState().entity.id,
        relatedToType: RelatedToType.Property,
      });
    }

    throw new Error('Not implemented');
  }

  @Action(EntityActions.ContactActions.CreateContactAssociation)
  createContactAssociation(context: StateContext<JupiterStateModel>, { contact }: { contact: Contact }) {
    if (context.getState().relatedToObject.name === RelatedToType.Property) {
      return this.service.createContactAssociation({
        contact,
        entityId: context.getState().entity.id,
        relatedToType: RelatedToType.Property,
      });
    }

    throw new Error('Not implemented');
  }

  @Action(EntityActions.LoanQuoteActions.Load)
  loadLoanQuotes(context: StateContext<JupiterStateModel>, { fields }: { fields: string[] }) {
    return this.service.getRelatedLoanQuotes({ entityId: context.getState().entity.id, fields }).pipe(
      tap((loanQuotes) => {
        context.patchState({ loanQuotes: { items: loanQuotes } });
      }),
    );
  }

  @Action(EntityActions.PropertyRentRollActions.LoadPropertyUnits)
  loadPropertyUnits(context: StateContext<JupiterStateModel>) {
    return this.service.getRelatedPropertyUnits({ entityId: context.getState().entity.id }).pipe(
      tap((propertyUnits) => {
        context.patchState({
          propertyUnits: {
            items: propertyUnits.map((propertyUnit) => {
              return {
                ...propertyUnit,
                ...propertyUnit.custom_field_values.reduce((p, c) => {
                  return { ...p, [c.name]: c.val };
                }, {}),
              };
            }),
          },
        });
      }),
    );
  }

  @Action(EntityActions.PropertyRentRollActions.UpdatePropertyUnit)
  updatePropertyUnit(context: StateContext<JupiterStateModel>, { propertyUnit }: { propertyUnit: PropertyUnit }) {
    return this.service.updatePropertyUnit(context.getState().entity.id, propertyUnit).pipe(
      tap((updatedPropertyUnit) => {
        context.patchState({
          entity: { ...context.getState().entity },
          propertyUnits: {
            items: context.getState().propertyUnits.items.map((propertyUnit) => {
              return propertyUnit.id === updatedPropertyUnit.id ? updatedPropertyUnit : propertyUnit;
            }),
          },
        });
      }),
    );
  }

  private getPermissionSlots(relatedTo: string, customObjects: CustomObject[]): any {
    const permissionPrefixMap: { [key: string]: string } = customObjects.reduce((p, c) => {
      let prefix = `${(c.id ? c.name : c.label_plural).toUpperCase().replace(/ /, '_')}_`;

      // this can be fixed on the BE by changing the label_plural of the object to "Leases"
      if (prefix === 'PROPERTY_TENANTS_') {
        prefix = 'LEASES_';
      }

      return { ...p, [c.name]: prefix };
    }, {});

    const permissionPrefix = permissionPrefixMap[relatedTo];
    const user = this.store.selectSnapshot(UserState.getUser);
    const result = user.roles[0].permissions_roles
      .map(({ permission }) => permission)
      .filter((permission) => permission.name.startsWith(permissionPrefix))
      .map(({ name }) => name)
      .reduce((p, c) => ({ ...p, [c.replace(permissionPrefix, '')]: c }), {});

    if (!Object.keys(result).length) {
      // when no permissions are configured, grant all slots so custom objects remain accessible
      return Object.values(PermissionSlot)
        .map((x) => `${permissionPrefix}${x}`)
        .reduce((p, c) => ({ ...p, [c.replace(permissionPrefix, '')]: c }), {});
    }

    return result;
  }

  private getRelationFieldName(
    tsObject: CustomObject,
    contextRelatedToObject: CustomObject,
    standardRelation?: { fieldName?: string; between?: { mainObject: RelatedToType; relatedObject: RelatedToType } },
  ): FieldDef {
    if (standardRelation?.fieldName) {
      return {
        name: standardRelation.fieldName,
        label: standardRelation.fieldName,
        type: 'number',
      };
    }

    const relationField = tsObject.fields.find(
      (x) => x.meta.record_type === contextRelatedToObject.name && x.name.startsWith(`cf_linked`),
    );

    return relationField;
  }

  private getRelationValue(
    tsObject: CustomObject,
    contextRelatedToObject: CustomObject,
    entity: Entity,
    standardRelation?: { fieldName?: string; between?: { mainObject: RelatedToType; relatedObject: RelatedToType } },
  ): string | string[] {
    if (standardRelation || tsObject.uiConfig?.isForStandardRelation) {
      return null;
    }

    const searchFieldName = contextRelatedToObject.fields.find(
      (x) => x.id === contextRelatedToObject.search_custom_field_id,
    )?.name;

    const relationField = this.getRelationFieldName(tsObject, contextRelatedToObject);

    let relationValue: string | string[] = entity
      ? `${entity.id}|${entity[searchFieldName] ?? entity.name ?? entity['title']}`
      : null;

    if (relationField.meta.multi_select && relationValue) {
      relationValue = [relationValue];
    }

    return relationValue;
  }

  private updateEntityData(newState: JupiterStateModel, stateContext: StateContext<JupiterStateModel>, entity: Entity) {
    const modifiedNewState = { ...newState };

    if (!stateContext.getState().context) {
      modifiedNewState.entity = entity;

      modifiedNewState.data.rows = stateContext.getState().data.rows.map((row) => {
        const rowIdAsNumber = Number(row.id);
        const entityIdAsNumber = Number(entity.id);
        const areBothNumbers = !isNaN(rowIdAsNumber) && !isNaN(entityIdAsNumber);

        if (!areBothNumbers) {
          console.error('Row id and entity id are not numbers', row.id, entity.id);
          return row;
        }

        if (areBothNumbers && rowIdAsNumber === entityIdAsNumber) {
          return { ...row, ...entity };
        }
        return row;
      });
    }

    return modifiedNewState;
  }
}
